import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Switch, toast, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, RotateCcw } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type LegacyProductionJobDTO } from "../../api";
import { LEGACY_JOB_VARIANT } from "../../components/badges";
import { EnumBadge, formatDate, objectUrl, Thumbnail, TwoLine } from "../../components/cells";
import { ErrorState } from "../../components/GuardedPage";

/**
 * The UFRA-era production queue, still fed by the legacy job importer.
 *
 * One of the two grids behind the switch on the production jobs page; the page
 * owns the header and the permission check, this owns the rows. Retry is only
 * offered on FAILED rows because the endpoint re-queues a job rather than
 * duplicating it — retrying a completed job would reprint it.
 *
 * Archived jobs (finished more than 30 days ago; their production image is
 * deleted) are hidden unless "Show archived" is on: they are most of the rows
 * and none can be acted on. Archiving keeps the job's last status, so an
 * archived job still reads COMPLETED or FAILED.
 */
export function LegacyProductionJobsGrid({ toolbarLead }: { toolbarLead?: ReactNode }) {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<LegacyProductionJobDTO[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["legacy-production-job"],
    queryFn: () => api.legacyProductionJobs.findAll(),
  });

  const rows = useMemo(
    () => (data ?? []).filter((row) => showArchived || !row.archived),
    [data, showArchived],
  );

  const artworkIds = useMemo(
    () =>
      Array.from(
        new Set(rows.map((row) => row.artwork_id).filter((id): id is number => Boolean(id))),
      ),
    [rows],
  );

  const artworks = useQuery({
    queryKey: ["artwork", "lazy", "by-ids", artworkIds],
    queryFn: () => api.lazyArtworks.findByIds(artworkIds),
    enabled: artworkIds.length > 0,
  });

  const artworkById = useMemo(
    () => new Map((artworks.data ?? []).map((entry) => [entry.id, entry])),
    [artworks.data],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["legacy-production-job"] });

  const cols = useMemo(() => {
    const c = defineColumns<LegacyProductionJobDTO>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => {
          const artwork = row.artwork_id ? artworkById.get(row.artwork_id) : undefined;
          return <Thumbnail url={objectUrl(artwork?.thumbnail?.image)} size="md" />;
        },
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => <EnumBadge value={row.status} variants={LEGACY_JOB_VARIANT} />,
        { headerKey: "column.status", filter: "select", priority: 1 },
      ),
      c.number("ufra_job", (row) => row.ufra_job_id_id, {
        headerKey: "column.ufra-job",
        priority: 2,
      }),
      c.custom(
        "artwork",
        (row) => {
          const artwork = row.artwork_id ? artworkById.get(row.artwork_id) : undefined;
          return artwork?.metadata.code ?? "";
        },
        (row) => {
          const artwork = row.artwork_id ? artworkById.get(row.artwork_id) : undefined;
          return (
            <TwoLine primary={artwork?.metadata.code} secondary={artwork?.metadata.title} />
          );
        },
        { headerKey: "column.artwork", priority: 1 },
      ),
      c.custom(
        "production_file",
        () => "",
        (row) =>
          objectUrl(row.production_image) ? (
            <a
              href={objectUrl(row.production_image)!}
              download
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
            >
              {t("value.download", "Download")}
            </a>
          ) : (
            <span className="text-ink-faint">{t("value.not-ready", "Not ready")}</span>
          ),
        { headerKey: "column.production-file", filter: "none", enableSorting: false, priority: 3 },
      ),
      c.text("created_at", (row) => formatDate(row.created_at), {
        headerKey: "column.created-at",
        priority: 4,
      }),
    ];
  }, [artworkById, t]);

  const retry = async () => {
    const failed = selected.filter((row) => row.status === "FAILED");
    if (failed.length === 0) return;
    const ok = await confirm({
      title: t("dialog.confirm.retry-legacy-job.title", "Retry legacy production job"),
      message: t(
        "dialog.confirm.retry-legacy-job.text",
        "Are you sure you want to retry the selected legacy production jobs?",
      ),
    });
    if (!ok) return;
    try {
      await api.legacyProductionJobs.retry(failed.map((row) => row.id));
      toast.success(
        t("notification.legacy-production-job.retried.success", "Successfully retried legacy production jobs"),
      );
      setSelected([]);
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.legacy-production-job.retried.error", "Failed to retry legacy production jobs"),
      );
    }
  };

  const cancel = async () => {
    if (selected.length === 0) return;
    const ok = await confirm({
      title: t("dialog.confirm.cancel-legacy-job.title", "Cancel legacy production job"),
      message: t(
        "dialog.confirm.cancel-legacy-job.text",
        "Are you sure you want to cancel the selected legacy production jobs?",
      ),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.legacyProductionJobs.cancel(selected.map((row) => row.id));
      toast.success(
        t("notification.legacy-production-job.cancelled.success", "Successfully cancelled legacy production jobs"),
      );
      setSelected([]);
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.legacy-production-job.cancelled.error", "Failed to cancel legacy production jobs"),
      );
    }
  };

  if (error) return <ErrorState title={t("navigation.item.production-jobs", "Production jobs")} error={error} />;

  return (
    <DataTable<LegacyProductionJobDTO>
      data={rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={isLoading}
      selectable
      onSelectionChange={setSelected}
      toolbarLead={
        <>
          {toolbarLead}
          <Switch
            checked={showArchived}
            onChange={setShowArchived}
            label={t("production.show-archived", "Show archived")}
          />
        </>
      }
      actions={[
        {
          label: t("menu.item.retry", "Retry"),
          icon: <RotateCcw className="size-4" />,
          write: true,
          disabled: selected.filter((row) => row.status === "FAILED").length === 0,
          onClick: () => void retry(),
        },
        {
          label: t("menu.item.cancel", "Cancel"),
          icon: <Ban className="size-4" />,
          variant: "danger",
          write: true,
          requiresSelection: true,
          onClick: () => void cancel(),
        },
      ]}
    />
  );
}
